import {
  createDictationSession,
  listMicrophones,
  type DictationError,
  type DictationSession,
  type DictationSessionOptions,
  type DictationStatus,
  type MicrophoneDevice,
} from "@z/speech";
import { toast } from "@z/ui";
import { useEffect, useRef, useState } from "react";

export type UseDictationOptions = {
  /** Mints a Deepgram temporary token; rejections may carry an API `code`. */
  getToken: () => Promise<{ token: string }>;
  /** Inserts committed text at the cursor. */
  insertText: (text: string) => void;
  /** Previews the phrase Deepgram is still revising at the cursor; `""` clears it. */
  setGhostText?: (text: string) => void;
  /** Text of the cursor's line, used to decide whether the first phrase needs a leading space. */
  getLine?: () => string;
  /** Preferred input; the system default when omitted. Applies to the next start. */
  deviceId?: string | undefined;
  /** Test seams. */
  createSession?: (options: DictationSessionOptions) => DictationSession;
  listDevices?: () => Promise<MicrophoneDevice[]>;
};

export type DictationControls = {
  status: DictationStatus;
  /** Starts when idle, stops when listening, cancels while connecting. */
  toggle: () => void;
  stop: () => void;
  /** Known inputs, filled by `refreshDevices` (which may prompt for the microphone once). */
  devices: MicrophoneDevice[];
  refreshDevices: () => void;
};

function apiCode(cause: unknown): string | undefined {
  return cause && typeof cause === "object" && "code" in cause && typeof cause.code === "string"
    ? cause.code
    : undefined;
}

export function describeDictationError(error: DictationError): string {
  switch (error.reason) {
    case "mic_denied":
      return "Microphone access was blocked";
    case "mic_unavailable":
      return "No microphone is available";
    case "token": {
      const code = apiCode(error.cause);
      if (code === "UNAUTHORIZED") return "Sign in to use dictation";
      if (code === "DICTATION_UNAVAILABLE") return "Dictation isn't configured on this server";
      return "Could not start dictation";
    }
    case "network":
    case "provider":
      return "Dictation connection lost";
  }
}

/**
 * One dictation session at a time for one editor. Final phrases go straight into the note
 * through `insertText`, so autosave and undo see ordinary edits; interim text stays in React
 * state for a live preview.
 */
export function useDictation(options: UseDictationOptions): DictationControls {
  const [status, setStatus] = useState<DictationStatus>("idle");
  const [devices, setDevices] = useState<MicrophoneDevice[]>([]);
  const session = useRef<DictationSession | null>(null);
  // The session outlives renders and holds a microphone: release it when the editor goes away.
  useEffect(() => () => session.current?.abort(), []);

  const toggle = () => {
    const current = session.current;
    if (current && current.status !== "idle" && current.status !== "error") {
      void current.stop();
      return;
    }
    const {
      getToken,
      insertText,
      setGhostText = () => undefined,
      getLine,
      deviceId,
      createSession = createDictationSession,
    } = options;
    let inserted = false;
    const next = createSession({
      getToken,
      ...(deviceId ? { deviceId } : {}),
      onSegment: (segment) => {
        if (!segment.isFinal) {
          setGhostText(segment.text.trim());
          return;
        }
        setGhostText("");
        const text = segment.text.trim();
        if (!text) return;
        const line = getLine?.() ?? "";
        const needsSpace = inserted || (line.length > 0 && !/\s$/.test(line));
        insertText(`${needsSpace ? " " : ""}${text}`);
        inserted = true;
      },
      onStatus: (nextStatus, error) => {
        if (session.current !== next) return;
        setStatus(nextStatus);
        if (nextStatus !== "listening") setGhostText("");
        if (error) toast({ title: describeDictationError(error), variant: "danger" });
      },
    });
    session.current = next;
    void next.start();
  };
  const stop = () => {
    void session.current?.stop();
  };
  const refreshDevices = () => {
    void (options.listDevices ?? listMicrophones)().then(setDevices, () => setDevices([]));
  };
  return { status, toggle, stop, devices, refreshDevices };
}
